import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FunnelIcon, SpinnerGapIcon, FloppyDiskIcon, TrashIcon, BellRingingIcon, BellSlashIcon } from "@phosphor-icons/react";
import { fetchScreener, fetchScreenerAlerts, saveScreenerAlert, deleteScreenerAlert } from "../lib/api";
import { getClientId } from "../lib/clientId";
import { TradeButton } from "../components/TradeButton";
import { useSeo } from "../lib/useSeo";
import { fmtUsd } from "../lib/format";

const FIELDS = [
  { key: "minMcap", label: "Min MCap $" }, { key: "maxMcap", label: "Max MCap $" },
  { key: "minVol", label: "Min 24h Vol $" }, { key: "maxVol", label: "Max 24h Vol $" },
  { key: "minLiq", label: "Min Liq $" }, { key: "maxLiq", label: "Max Liq $" },
  { key: "minAgeH", label: "Min Age (h)" }, { key: "maxAgeH", label: "Max Age (h)" },
];
const SORTS = [["volume", "Volume"], ["mcap", "MCap"], ["liquidity", "Liquidity"], ["newest", "Newest"], ["score", "Score"]];
const EMPTY = Object.fromEntries(FIELDS.map((f) => [f.key, ""]));
const PRESET_KEY = "screener_presets";

export default function Screener() {
  useSeo({ title: "Coin Screener — Filter Live Solana Coins | AlphaApeing", description: "Filter the live Solana meme universe by market cap, volume, liquidity and age ranges." });
  const [form, setForm] = useState(EMPTY);
  const [sort, setSort] = useState("volume");
  const [applied, setApplied] = useState({ ...EMPTY, sort: "volume" });
  const [presets, setPresets] = useState(() => { try { return JSON.parse(localStorage.getItem(PRESET_KEY) || "[]"); } catch { return []; } });
  const clientId = getClientId();
  const qc = useQueryClient();
  const alertsQ = useQuery({ queryKey: ["screenerAlerts", clientId], queryFn: () => fetchScreenerAlerts(clientId) });
  const serverAlerts = alertsQ.data?.alerts || [];
  const telegramReady = alertsQ.data?.telegramReady;

  const toggleAlert = async (p) => {
    const existing = serverAlerts.find((a) => a.name === p.name);
    try {
      if (existing) {
        await deleteScreenerAlert(clientId, existing.id);
        toast.success(`Telegram pings OFF for “${p.name}”`);
      } else {
        await saveScreenerAlert(clientId, p.name, p.form);
        toast.success(telegramReady
          ? `Telegram pings ON — you'll get pinged when a NEW coin matches “${p.name}”`
          : `Alert saved — connect Telegram on the Alerts page to receive pings`);
      }
      qc.invalidateQueries({ queryKey: ["screenerAlerts", clientId] });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to update alert"); }
  };

  const q = useQuery({
    queryKey: ["screener", applied],
    queryFn: () => fetchScreener(applied),
    refetchInterval: 60000,
  });

  const run = () => setApplied({ ...form, sort });
  const savePreset = () => {
    const name = window.prompt("Name this preset (e.g. 'My sweet spot'):");
    if (!name) return;
    const next = [...presets.filter((p) => p.name !== name), { name, form, sort }];
    setPresets(next);
    localStorage.setItem(PRESET_KEY, JSON.stringify(next));
  };
  const loadPreset = (p) => { setForm(p.form); setSort(p.sort); setApplied({ ...p.form, sort: p.sort }); };
  const delPreset = (name) => {
    const next = presets.filter((p) => p.name !== name);
    setPresets(next);
    localStorage.setItem(PRESET_KEY, JSON.stringify(next));
  };

  const coins = q.data?.coins || [];
  return (
    <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-6 space-y-6 animate-fade-up" data-testid="screener-page">
      <div>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 flex items-center justify-center bg-neon text-black"><FunnelIcon size={24} weight="fill" /></div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">COIN <span className="text-neon">SCREENER</span></h1>
        </div>
        <p className="text-muted-foreground mt-3 text-sm max-w-3xl">
          Set your ranges — market cap, volume, liquidity, age — and pull every live Solana coin that fits. Leave a field blank to skip it. Save your sweet spot as a preset, then tap the bell to get a Telegram ping whenever a new coin matches it.
        </p>
      </div>

      <div className="border border-white/10 bg-card p-5 space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {FIELDS.map((f) => (
            <label key={f.key} className="space-y-1">
              <span className="text-[10px] font-mono uppercase text-muted-foreground">{f.label}</span>
              <input
                data-testid={`screener-${f.key}`}
                type="number" min="0" placeholder="—"
                value={form[f.key]}
                onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                className="w-full bg-black/40 border border-white/15 px-2.5 py-2 text-sm font-mono focus:border-neon/60 outline-none"
              />
            </label>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex border border-white/10">
            {SORTS.map(([k, l]) => (
              <button key={k} onClick={() => setSort(k)} data-testid={`screener-sort-${k}`}
                className={`px-3 py-2 text-xs font-medium transition-colors ${sort === k ? "bg-neon/10 text-neon" : "text-muted-foreground hover:text-white"}`}>{l}</button>
            ))}
          </div>
          <button data-testid="screener-run" onClick={run} className="px-6 py-2 bg-neon text-black text-sm font-bold hover:bg-neon/90 transition-colors">Screen</button>
          <button data-testid="screener-save-preset" onClick={savePreset} className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold border border-white/15 text-muted-foreground hover:text-white transition-colors">
            <FloppyDiskIcon size={14} /> Save preset
          </button>
          {q.data && <span className="ml-auto text-xs font-mono text-muted-foreground" data-testid="screener-count">{q.data.matched} match · {q.data.universe} coins scanned</span>}
        </div>
        {presets.length > 0 && (
          <div className="pt-1 border-t border-white/5 space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] font-mono uppercase text-muted-foreground">Presets</span>
              {presets.map((p) => {
                const alertOn = serverAlerts.some((a) => a.name === p.name);
                return (
                  <span key={p.name} className="flex items-center gap-1 border border-neon/30 text-neon text-xs px-2 py-1">
                    <button onClick={() => loadPreset(p)} className="hover:underline">{p.name}</button>
                    <button
                      data-testid={`preset-alert-${p.name}`}
                      onClick={() => toggleAlert(p)}
                      title={alertOn ? "Telegram pings ON — click to turn off" : "Ping me on Telegram when a new coin matches"}
                      className={alertOn ? "text-neon" : "text-muted-foreground hover:text-neon"}
                    >
                      {alertOn ? <BellRingingIcon size={12} weight="fill" /> : <BellSlashIcon size={12} />}
                    </button>
                    <button onClick={() => delPreset(p.name)} className="text-muted-foreground hover:text-danger"><TrashIcon size={11} /></button>
                  </span>
                );
              })}
            </div>
            {serverAlerts.length > 0 && !telegramReady && (
              <p className="text-[11px] text-warn font-mono" data-testid="screener-alert-tg-hint">
                ⚠ Alerts saved but Telegram isn't connected — <Link to="/alerts" className="underline hover:text-neon">connect it on the Alerts page</Link> to receive pings.
              </p>
            )}
            {serverAlerts.length > 0 && telegramReady && (
              <p className="text-[11px] text-muted-foreground font-mono" data-testid="screener-alert-status">
                🔔 {serverAlerts.length} preset{serverAlerts.length > 1 ? "s" : ""} pinging Telegram when a new coin matches (checked every ~7 min).
              </p>
            )}
          </div>
        )}
      </div>

      {q.isLoading ? (
        <div className="py-16 flex justify-center"><SpinnerGapIcon size={28} className="animate-spin text-neon" /></div>
      ) : coins.length === 0 ? (
        <div className="border border-white/10 bg-card py-16 text-center text-muted-foreground font-mono text-sm" data-testid="screener-empty">
          No coins match those ranges right now — widen a filter and hit Screen again.
        </div>
      ) : (
        <div className="border border-white/10 bg-card overflow-x-auto" data-testid="screener-results">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase text-muted-foreground border-b border-white/10">
                <th className="py-2.5 px-3">Coin</th><th className="py-2.5 px-3">MCap</th><th className="py-2.5 px-3">Liq</th>
                <th className="py-2.5 px-3">24h Vol</th><th className="py-2.5 px-3">Age</th><th className="py-2.5 px-3">24h %</th>
                <th className="py-2.5 px-3">Score</th><th className="py-2.5 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {coins.map((c) => (
                <tr key={c.address} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="py-2 px-3">
                    <Link to={`/coin/${c.address}`} className="flex items-center gap-2 hover:text-neon">
                      {c.image && <img src={c.image} alt="" className="w-6 h-6 object-cover border border-white/10" onError={(e) => { e.target.style.display = "none"; }} />}
                      <span className="font-bold">${(c.symbol || "?").toUpperCase()}</span>
                    </Link>
                  </td>
                  <td className="py-2 px-3 font-mono text-xs">{fmtUsd(c.marketCap)}</td>
                  <td className="py-2 px-3 font-mono text-xs">{fmtUsd(c.liquidity)}</td>
                  <td className="py-2 px-3 font-mono text-xs">{fmtUsd((c.volume || {}).h24)}</td>
                  <td className="py-2 px-3 font-mono text-xs text-muted-foreground">{c.ageHours != null ? (c.ageHours < 48 ? `${Math.round(c.ageHours)}h` : `${Math.round(c.ageHours / 24)}d`) : "—"}</td>
                  <td className={`py-2 px-3 font-mono text-xs ${((c.priceChange || {}).h24 || 0) >= 0 ? "text-neon" : "text-[#FF3366]"}`}>{(c.priceChange || {}).h24 ?? "—"}%</td>
                  <td className="py-2 px-3 font-mono text-xs">{c.score?.total ?? "—"}</td>
                  <td className="py-2 px-3"><TradeButton coin={{ address: c.address, symbol: c.symbol, url: c.url }} size="sm" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
